import { baseApi } from "./api";

export const invoiceApi = baseApi.injectEndpoints({
	endpoints: (build) => ({
		getProjectInvoices: build.query({
			query: ({
				projectId,
				page = 1,
				limit = 50,
				search,
				ticketCategoryId,
				status,
			}) => {
				const params = { limit, offset: (page - 1) * limit };
				if (search) params.search = search;
				if (ticketCategoryId) params.ticketCategoryId = ticketCategoryId;
				if (status) params.status = status;
				return {
					url: `projects/${projectId}/invoices`,
					params,
				};
			},
			// Same cache entry across pages - items accumulate.
			serializeQueryArgs: ({ queryArgs }) => {
				const { projectId, search, ticketCategoryId, status } = queryArgs;
				return { projectId, search, ticketCategoryId, status };
			},
			merge: (currentCache, incoming, { arg }) => {
				if (arg.page === 1 || !currentCache) {
					return incoming;
				}
				return {
					...incoming,
					items: [
						...(currentCache.items || []),
						...(incoming.items || []),
					],
				};
			},
			forceRefetch: ({ currentArg, previousArg }) => {
				if (!previousArg) return true;
				return (
					currentArg?.page !== previousArg?.page ||
					currentArg?.search !== previousArg?.search ||
					currentArg?.ticketCategoryId !==
						previousArg?.ticketCategoryId ||
					currentArg?.status !== previousArg?.status
				);
			},
			providesTags: (result, _e, { projectId }) => [
				{ type: "Invoice", id: "LIST" },
				{ type: "Invoice", id: `project-${projectId}` },
				...((result?.items ?? []).map((i) => ({
					type: "Invoice",
					id: i.id,
				}))),
			],
		}),
		deleteInvoice: build.mutation({
			query: ({ projectId, invoiceId }) => ({
				url: `projects/${projectId}/invoices/${invoiceId}`,
				method: "DELETE",
			}),
			invalidatesTags: (_r, _e, { projectId, invoiceId }) => [
				{ type: "Invoice", id: invoiceId },
				{ type: "Invoice", id: "LIST" },
				{ type: "Invoice", id: `project-${projectId}` },
			],
		}),
		saveRejectedInvoice: build.mutation({
			query: ({ projectId, ...body }) => ({
				url: `projects/${projectId}/rejected-invoices`,
				method: "POST",
				body,
			}),
			invalidatesTags: (_r, _e, { projectId }) => [
				{ type: "Invoice", id: "LIST" },
				{ type: "Invoice", id: `project-${projectId}` },
			],
		}),
		getInvoiceByIdV2: build.query({
			query: ({ projectId, invoiceId }) => ({
				url: `projects/${projectId}/invoices/${invoiceId}/v2`,
			}),
			providesTags: (_r, _e, { invoiceId }) => [
				{ type: "Invoice", id: invoiceId },
			],
		}),
		getInvoicesByTicketCategory: build.query({
			query: ({ projectId, ticketCategoryId, currentInvoiceId }) => ({
				url: `projects/${projectId}/ticket-categories/${ticketCategoryId}/invoices`,
				params: currentInvoiceId ? { currentInvoiceId } : undefined,
			}),
			// Also subscribe to the broad LIST tag so deleting/saving an invoice
			// anywhere (the delete mutation has no ticketCategoryId to target the
			// category-scoped tag) busts this cache - otherwise Step 1 keeps
			// showing a draft that was already deleted from the listing screen.
			providesTags: (_r, _e, { ticketCategoryId }) => [
				{ type: "Invoice", id: "LIST" },
				{ type: "Invoice", id: `category-${ticketCategoryId}` },
			],
		}),
		getPreviousInvoicePositionsV2: build.query({
			query: ({ projectId, ticketCategoryId, currentInvoiceId = null }) => {
				const params = currentInvoiceId
					? { currentInvoiceId }
					: undefined;
				return {
					url: `projects/${projectId}/invoices/previous-positions/${ticketCategoryId}/v2`,
					params,
				};
			},
			providesTags: (_r, _e, { ticketCategoryId }) => [
				{ type: "Invoice", id: "LIST" },
				{ type: "Invoice", id: `previous-${ticketCategoryId}` },
			],
		}),
		saveInvoiceV2: build.mutation({
			query: ({ projectId, ...body }) => ({
				url: `projects/${projectId}/invoices/v2`,
				method: "POST",
				body,
			}),
			invalidatesTags: (_r, _e, { projectId }) => [
				{ type: "Invoice", id: "LIST" },
				{ type: "Invoice", id: `project-${projectId}` },
			],
		}),
		updateInvoiceV2: build.mutation({
			query: ({ projectId, invoiceId, ...body }) => ({
				url: `projects/${projectId}/invoices/${invoiceId}/v2`,
				method: "PUT",
				body,
			}),
			invalidatesTags: (_r, _e, { projectId, invoiceId }) => [
				{ type: "Invoice", id: invoiceId },
				{ type: "Invoice", id: "LIST" },
				{ type: "Invoice", id: `project-${projectId}` },
			],
		}),
	}),
});

export const {
	useGetProjectInvoicesQuery,
	useDeleteInvoiceMutation,
	useSaveRejectedInvoiceMutation,
	useGetInvoiceByIdV2Query,
	useGetInvoicesByTicketCategoryQuery,
	useGetPreviousInvoicePositionsV2Query,
	useSaveInvoiceV2Mutation,
	useUpdateInvoiceV2Mutation,
} = invoiceApi;
